import { FC, lazy, Suspense, useEffect, useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";
import { clipboardUtils, FixedList, IInputField, useTheme } from "cayo.ui";
import { UNIQUE_GRID_OBJECT_PROP } from "@cayo/admin-components/components/grids/constants";
import { normalizePropertyNames } from "@cayo/admin-components/components/grids/utils";
import { IObjectSelectionGrid } from "@cayo/shared/api/types/schema";
import { withHidden } from "@cayo/shared/components/Hoc/withHidden";
import GlobalObjectSearch from "./GlobalObjectSearch";
import { IMembersLookupData } from "./types";
import { useObjectSelectionGrid } from "./useObjectSelectionGrid";
import { CopyCellContainer } from "../../../components/CellWithCopy";
import renderGridCell from "../../../components/GridContainer/renderers/gridCellRenderer";

const ObjectSelectionDialog = lazy(
  () => import("../../dialogs/ObjectSelectionDialog/ObjectSelectionDialog")
);

type ObjectSelectionGridProps = IObjectSelectionGrid & {
  membersLookupData: IMembersLookupData;
} & IInputField;

const ObjectSelectionGrid: FC<ObjectSelectionGridProps> = ({
  name,
  columns,
  value,
  label,
  setValue,
  noItemsText,
  searchScopes,
  idPropName,
  globalSearchDescriptor,
  membersLookupData,
  readOnly,
  allowRemove,
  hideLabel,
  modalOptions,
  actions,
}) => {
  const listItemKey = idPropName || UNIQUE_GRID_OBJECT_PROP; // TODO: get from backend!
  const { officeTheme } = useTheme();

  // store the initial items of the grid, which we compare with added and removed items, deps array must be empty
  const initialItems = useMemo(() => {
    if (value) {
      const items = Array.isArray(value) ? value : [value];
      const fixedItems = normalizePropertyNames(items);
      return fixedItems;
    }
    return [];
  }, []);
  const { commands, items, onAddItems, isPickerVisible, hidePicker, getAddedAndRemovedMembers } =
    useObjectSelectionGrid(initialItems, readOnly, allowRemove, listItemKey, columns, actions);

  useEffect(() => {
    const result = getAddedAndRemovedMembers();
    // set empty added and removed members on initial render
    setValue(result);
  }, [items.length]);

  return (
    <Stack>
      {isPickerVisible && (
        <Suspense fallback={null}>
          <ObjectSelectionDialog
            searchScopes={searchScopes}
            modalOptions={modalOptions}
            keyProp="id"
            onClose={hidePicker}
            onSelected={async (items) => {
              onAddItems(items);
              hidePicker();
            }}
          />
        </Suspense>
      )}
      {globalSearchDescriptor && (
        <GlobalObjectSearch
          globalSearchDescriptor={globalSearchDescriptor}
          membersLookupData={membersLookupData}
          addObjects={onAddItems}
        />
      )}
      <FixedList
        name={name!}
        label={label}
        columns={columns}
        value={items}
        hideLabel={hideLabel}
        layoutMode="fixedColumns"
        showAdjustColumnsButton
        selectionMode="multiple"
        setValue={(v) => {
          setValue(v);
        }}
        commands={commands}
        onCommandStateChange={(item, selectedItems) => {
          if (item.key === "remove" && selectedItems?.length) {
            if (selectedItems.some((i) => i.cFlags === "NoDelete")) {
              item.disabled = true;
            }
          }
        }}
        readOnly={readOnly && !allowRemove}
        noItemsText={items?.length ? undefined : noItemsText}
        onRenderItemColumn={(item, i, col) => {
          const isPrimary = item.cFlags === "NoDelete";
          const isNameCol = col?.fieldName === "name";

          if (isPrimary && isNameCol) {
            return (
              <CopyCellContainer onClick={() => clipboardUtils.copyToClipboard(item.name)}>
                <Icon
                  iconName="Lock"
                  styles={{ root: { color: officeTheme.palette.themePrimary, marginRight: 4 } }}
                />
                <Text block nowrap variant="small">
                  {item.name}
                </Text>
              </CopyCellContainer>
            );
          }

          return renderGridCell(item, i!, col);
        }}
        listItemKey={listItemKey}
      />
    </Stack>
  );
};

export default withHidden(ObjectSelectionGrid);
